import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, AppAssets } from '../contexts/AuthContext';
import { User, UserRole, LoginHistoryLog, SessionHistoryLog } from '../types';
import { useFlight } from '../contexts/FlightContext';
import { updateUserRole } from '../services/userService';
import { clearReportsHistory, clearLoginHistory, clearNewSessionHistory } from '../services/systemService';
import { EyeIcon, EyeSlashIcon, CheckIcon, XCircleIcon, TrashIcon, UserPlusIcon, PencilIcon, SpinnerIcon, SpeakerWaveIcon } from '../components/Icons';
import { database } from '../services/firebase';
import { ref, onValue, set } from 'firebase/database';
import NewsInputField from '../components/NewsInputField';
import IconWizard from '../components/IconWizard';

// #region Sub-components for Admin Screen

const AssetUploader: React.FC<{
  assetKey: keyof AppAssets;
  assetUrl?: string;
  updateAsset: (assetKey: keyof AppAssets, dataUrl: string | null) => Promise<void>;
  assetType: 'image' | 'audio';
  label: string;
  showNotification: (message: string, type?: 'success' | 'error') => void;
  keepTransparency?: boolean;
}> = React.memo(({ assetKey, assetUrl, updateAsset, assetType, label, showNotification, keepTransparency = false }) => {
    const { defaultAppAssets } = useAuth();
    const [isUploading, setIsUploading] = useState(false);
    const [backgroundColor, setBackgroundColor] = useState('#1e293b');
    const inputRef = useRef<HTMLInputElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setIsUploading(true);

        const reader = new FileReader();
        reader.onerror = () => {
            setIsUploading(false);
            showNotification('Failed to read file.', 'error');
        };

        if (assetType === 'audio' || (assetType === 'image' && keepTransparency)) {
             reader.onload = async (e) => {
                try {
                    await updateAsset(assetKey, e.target?.result as string);
                    showNotification(`${label} updated successfully.`, 'success');
                } catch (err) {
                    showNotification(`Failed to update ${label}.`, 'error');
                } finally {
                    setIsUploading(false);
                }
            };
            reader.readAsDataURL(file);
        } else { // Image processing logic for non-transparent images
            reader.onload = (e) => {
                const img = new Image();
                img.onload = async () => {
                    const canvas = canvasRef.current;
                    if (!canvas) {
                        setIsUploading(false);
                        showNotification('Canvas element not found for processing.', 'error');
                        return;
                    }
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        setIsUploading(false);
                        showNotification('Could not get canvas context.', 'error');
                        return;
                    }
                    
                    const size = 128; // Standardize in-app icons to 128x128
                    canvas.width = size;
                    canvas.height = size;

                    // Apply background color unless transparency is requested
                    if (!keepTransparency) {
                        ctx.fillStyle = backgroundColor;
                        ctx.fillRect(0, 0, size, size);
                    }

                    // Draw image scaled to fit within the canvas while maintaining aspect ratio
                    const imgAspectRatio = img.width / img.height;
                    let drawWidth = size;
                    let drawHeight = size;
                    if (imgAspectRatio > 1) { // Landscape
                        drawHeight = size / imgAspectRatio;
                    } else { // Portrait or square
                        drawWidth = size * imgAspectRatio;
                    }
                    const xOffset = (size - drawWidth) / 2;
                    const yOffset = (size - drawHeight) / 2;
                    ctx.drawImage(img, xOffset, yOffset, drawWidth, drawHeight);

                    const processedDataUrl = canvas.toDataURL('image/png');

                    try {
                        await updateAsset(assetKey, processedDataUrl);
                        showNotification(`${label} processed and updated successfully.`, 'success');
                    } catch (err) {
                        showNotification(`Failed to update ${label}.`, 'error');
                    } finally {
                        setIsUploading(false);
                    }
                };
                img.onerror = () => {
                    setIsUploading(false);
                    showNotification('Failed to load image for processing.', 'error');
                };
                img.src = e.target?.result as string;
            };
            reader.readAsDataURL(file);
        }
    }, [assetKey, updateAsset, label, showNotification, assetType, backgroundColor, keepTransparency]);

    const handleRemove = useCallback(async () => {
        if (window.confirm(`Are you sure you want to remove the ${label}?`)) {
            try {
                await updateAsset(assetKey, null);
                showNotification(`${label} removed successfully.`, 'success');
            } catch (err) {
                 showNotification(`Failed to remove ${label}.`, 'error');
            }
        }
    }, [assetKey, updateAsset, label, showNotification]);
    
    const handleTestSound = useCallback(() => {
        const soundToPlay = assetUrl || defaultAppAssets.notificationSound;
        if (soundToPlay) {
            const audio = new Audio(soundToPlay);
            audio.play().then(() => {
                showNotification("Test notification sound played!", 'success');
            }).catch(e => {
                console.error("Error playing test sound:", e);
                showNotification("Could not play sound. Please interact with the page first.", 'error');
            });
        } else {
            showNotification("No notification sound configured to test.", 'error');
        }
    }, [assetUrl, defaultAppAssets.notificationSound, showNotification]);


    return (
        <div className="relative bg-slate-700/50 p-3 rounded-lg flex flex-col items-center text-center h-full">
             <canvas ref={canvasRef} style={{ display: 'none' }} />
             {isUploading && (
                <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center rounded-lg z-10">
                    <SpinnerIcon className="w-10 h-10 text-white animate-spin-slow" />
                </div>
            )}
            <h4 className="text-md font-semibold text-white mb-2">{label}</h4>
            <div className="h-24 w-full flex items-center justify-center bg-slate-800 rounded mb-2">
                {assetUrl ? (
                    assetType === 'image' ? (
                        <img src={assetUrl} alt={label} className="max-h-full max-w-full object-contain p-1" />
                    ) : (
                        <audio controls src={assetUrl} className="w-full h-10">Your browser does not support the audio element.</audio>
                    )
                ) : (
                    <span className="text-slate-500 text-xs">No file uploaded</span>
                )}
            </div>
            {assetType === 'image' && !keepTransparency && (
                <div className="flex items-center gap-2 justify-center mb-2">
                    <label htmlFor={`${assetKey}-bg-color`} className="text-xs text-slate-400">Background:</label>
                    <input
                        id={`${assetKey}-bg-color`}
                        type="color"
                        value={backgroundColor}
                        onChange={(e) => setBackgroundColor(e.target.value)}
                        className="w-8 h-6 rounded border-none bg-slate-700 cursor-pointer"
                        title="Select background color for transparent images"
                    />
                </div>
            )}
            <input
                type="file"
                accept={`${assetType}/*`}
                ref={inputRef}
                onChange={handleFileChange}
                className="hidden"
            />
            <div className="flex items-center gap-2 mt-auto w-full">
                <button
                    onClick={() => inputRef.current?.click()}
                    disabled={isUploading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-3 rounded-lg text-xs disabled:bg-slate-500"
                >
                    {isUploading ? 'Processing...' : (assetType === 'image' && !keepTransparency ? 'Upload & Process' : 'Change')}
                </button>
                 {assetType === 'audio' && (
                    <button
                        onClick={handleTestSound}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2 px-3 rounded-lg text-xs flex items-center justify-center gap-1"
                        title="Play notification sound"
                    >
                        <SpeakerWaveIcon className="w-4 h-4" />
                        Test
                    </button>
                )}
                {assetUrl && (
                    <button
                        onClick={handleRemove}
                        className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-3 rounded-lg text-xs"
                    >
                        Remove
                    </button>
                )}
            </div>
        </div>
    );
});

const UserManagementPanel: React.FC<{
    users: User[];
    currentUser: User | null;
    isSuperAdmin: boolean;
    onShowCreateUser: () => void;
}> = ({ users, currentUser, isSuperAdmin, onShowCreateUser }) => {
    // Component logic remains large, but now isolated. For this request, keeping it here.
    const { showNotification } = useFlight();
    const { deleteUser, updateUserPassword } = useAuth();
    const [passwordVisibility, setPasswordVisibility] = useState<{ [key: string]: boolean }>({});
    const [editingUserId, setEditingUserId] = useState<string | null>(null);
    const [newPassword, setNewPassword] = useState('');
    const [userToDelete, setUserToDelete] = useState<User | null>(null);

    const togglePasswordVisibility = (userId: string) => {
        setPasswordVisibility(prev => ({ ...prev, [userId]: !prev[userId] }));
    };

    const handleRoleChange = async (userId: string, newRole: UserRole) => {
        await updateUserRole(userId, newRole);
        showNotification("User role updated.", "success");
    };

    const handleUpdatePassword = async (userId: string) => {
        if (newPassword.length < 6) {
            showNotification('Password must be at least 6 characters long.', 'error');
            return;
        }
        await updateUserPassword(userId, newPassword);
        setEditingUserId(null);
        setNewPassword('');
        showNotification("Password updated successfully.", "success");
    };

    const handleDeleteUser = async (userId: string) => {
        await deleteUser(userId);
        showNotification("User deleted.", "success");
        setUserToDelete(null);
    };

    const filteredUsers = users.filter(u => {
      if (u.id === currentUser?.id) return false;
      if (!isSuperAdmin && u.username === 'JBP') return false;
      return true;
    });

    return (
      <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <h2 className="text-xl font-bold text-blue-400">User Management</h2>
          <button onClick={onShowCreateUser} className="flex items-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg text-sm w-full sm:w-auto justify-center">
            <UserPlusIcon className="w-5 h-5 mr-2"/>
            Create New User
          </button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-700">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Username</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Password</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {filteredUsers.map(user => (
                    <tr key={user.id} className="hover:bg-slate-800/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-white">{user.username}</div>
                        <div className="text-xs text-slate-400">{user.email || 'No email'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                        <select
                          value={user.role}
                          onChange={(e) => handleRoleChange(user.id, e.target.value as UserRole)}
                          className="py-1 px-2 text-white"
                          disabled={!isSuperAdmin && user.role === UserRole.ADMIN}
                        >
                          {Object.values(UserRole)
                            .filter(role => isSuperAdmin || role !== UserRole.ADMIN || role === user.role)
                            .map(role => <option key={role} value={role}>{role}</option>)}
                        </select>
                      </td>
                       <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                        {editingUserId === user.id ? (
                             <div className="flex items-center gap-2">
                                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-32" placeholder="New Password"/>
                                <button onClick={() => handleUpdatePassword(user.id)} className="text-green-400 hover:text-green-300"><CheckIcon className="w-5 h-5"/></button>
                                <button onClick={() => setEditingUserId(null)} className="text-red-400 hover:text-red-300"><XCircleIcon className="w-5 h-5"/></button>
                             </div>
                        ) : (
                            <div className="flex items-center gap-2">
                               <span>{passwordVisibility[user.id] ? user.password : '********'}</span>
                                {(isSuperAdmin || user.role !== UserRole.ADMIN) && (
                                  <>
                                    <button onClick={() => togglePasswordVisibility(user.id)} className="text-slate-400 hover:text-white">
                                        {passwordVisibility[user.id] ? <EyeSlashIcon className="w-5 h-5"/> : <EyeIcon className="w-5 h-5"/>}
                                    </button>
                                    <button onClick={() => { setEditingUserId(user.id); setNewPassword('');}} className="text-blue-400 hover:text-white">
                                      <PencilIcon className="w-4 h-4"/>
                                    </button>
                                  </>
                                )}
                            </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {(isSuperAdmin || user.role !== UserRole.ADMIN) &&
                            <button onClick={() => setUserToDelete(user)} className="text-red-500 hover:text-red-400">
                                <TrashIcon className="w-5 h-5"/>
                            </button>
                          }
                      </td>
                    </tr>
              ))}
            </tbody>
          </table>
        </div>
        {userToDelete && (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-slide-up-fade-in">
            <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-md text-center border border-slate-700">
                <h2 className="text-xl font-bold text-white mb-4">Confirm Deletion</h2>
                <p className="text-slate-300 mb-6">
                Are you sure you want to permanently delete user <span className="font-bold text-amber-400">{userToDelete.username}</span>? This action cannot be undone.
                </p>
                <div className="flex justify-center gap-4">
                <button onClick={() => setUserToDelete(null)} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-6 rounded-lg transition-colors">Cancel</button>
                <button onClick={() => handleDeleteUser(userToDelete.id)} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg transition-colors">Confirm Delete</button>
                </div>
            </div>
            </div>
        )}
      </div>
    );
};

// #endregion

const AdminScreen: React.FC = () => {
  const { user: currentUser, users, isRegistrationOpen, toggleRegistration, authCodes, updateAuthCodes, createUser, securityScanPercentage, updateSecurityScanPercentage, updateUserPassword, appAssets, updateAsset, isMultiFlightEnabled, toggleMultiFlight, vapidPublicKey, updateVapidPublicKey } = useAuth();
  const { clearNewsFeed, showNotification } = useFlight();
  
  const [flightNumbers, setFlightNumbers] = useState<string[]>([]);
  const [newFlightNumber, setNewFlightNumber] = useState('');
  
  const [newAuthCode, setNewAuthCode] = useState('');
  const [selectedRoleForCode, setSelectedRoleForCode] = useState<UserRole>(UserRole.AGENT);

  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', email: '', role: UserRole.AGENT });
  const [showIconWizard, setShowIconWizard] = useState(false);
  
  const [groupedLoginHistory, setGroupedLoginHistory] = useState<{ [date: string]: (LoginHistoryLog & { key: string })[] }>({});
  const [sortedHistoryDates, setSortedHistoryDates] = useState<string[]>([]);
  const [sessionHistory, setSessionHistory] = useState<SessionHistoryLog[]>([]);
  
  const [newScanPercentage, setNewScanPercentage] = useState(securityScanPercentage);
  const [superAdminPassword, setSuperAdminPassword] = useState('');
  const [vapidKeyInput, setVapidKeyInput] = useState(vapidPublicKey || '');
  
  const isSuperAdmin = currentUser?.username === 'JBP';
  const isAdmin = currentUser?.role === UserRole.ADMIN;
  
  useEffect(() => {
    setVapidKeyInput(vapidPublicKey || '');
  }, [vapidPublicKey]);

  useEffect(() => {
    setNewScanPercentage(securityScanPercentage);
  }, [securityScanPercentage]);

  useEffect(() => {
    const flightNumbersRef = ref(database, 'config/flightNumbers');
    const unsubscribeFlights = onValue(flightNumbersRef, (snapshot) => {
        setFlightNumbers(snapshot.val() || []);
    });
    
    const historyRef = ref(database, 'loginHistory');
    const unsubscribeHistory = onValue(historyRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
            const logs: (LoginHistoryLog & { key: string })[] = Object.entries(data).map(([key, value]) => ({
                key,
                ...((value as unknown) as LoginHistoryLog)
            })).filter(log => log.username && log.action && typeof log.timestamp === 'number');

            const groups: { [date: string]: typeof logs } = {};
            logs.forEach(log => {
                const date = new Date(log.timestamp).toISOString().split('T')[0];
                if (!groups[date]) {
                    groups[date] = [];
                }
                groups[date].push(log);
            });

            for (const date in groups) {
                groups[date].sort((a, b) => b.timestamp - a.timestamp);
            }
            
            setGroupedLoginHistory(groups);
            setSortedHistoryDates(Object.keys(groups).sort((a, b) => new Date(b).getTime() - new Date(a).getTime()));
        } else {
            setGroupedLoginHistory({});
            setSortedHistoryDates([]);
        }
    });

    const sessionHistoryRef = ref(database, 'newSessionHistory');
    const unsubscribeSessions = onValue(sessionHistoryRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
            const logs: SessionHistoryLog[] = Object.entries(data).map(([key, value]) => ({
                key,
                ...((value as any) as Omit<SessionHistoryLog, 'key'>)
            })).sort((a,b) => b.timestamp - a.timestamp);
            setSessionHistory(logs);
        } else {
            setSessionHistory([]);
        }
    });

    return () => {
        unsubscribeFlights();
        unsubscribeHistory();
        unsubscribeSessions();
    };
  }, []);
  
  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.password) {
        showNotification("Username and password are required.", 'error');
        return;
    }
    if (newUser.password.length < 6) {
        showNotification('Password must be at least 6 characters long.', 'error');
        return;
    }
    const result = await createUser(newUser);
    if (result.success) {
        showNotification(result.message, 'success');
        setNewUser({ username: '', password: '', email: '', role: UserRole.AGENT });
        setShowCreateUser(false);
    } else {
        showNotification(result.message, 'error');
    }
  };
  
  const addFlightNumber = async () => {
    if (newFlightNumber && !flightNumbers.includes(newFlightNumber)) {
        const updatedFlights = [...flightNumbers, newFlightNumber];
        try {
            await set(ref(database, 'config/flightNumbers'), updatedFlights);
            setNewFlightNumber('');
            showNotification('Flight number added successfully.');
        } catch(e) {
            showNotification('Failed to add flight number.', 'error');
            console.error(e);
        }
    }
  };

  const removeFlightNumber = async (flightToRemove: string) => {
    const updatedFlights = flightNumbers.filter(f => f !== flightToRemove);
    try {
        await set(ref(database, 'config/flightNumbers'), updatedFlights);
        showNotification('Flight number removed.');
    } catch(e) {
        showNotification('Failed to remove flight number.', 'error');
        console.error(e);
    }
  };
  
  const handleAddCode = async () => {
    if (newAuthCode && !authCodes[newAuthCode]) {
        const newCodes = { ...authCodes, [newAuthCode]: selectedRoleForCode };
        try {
            await updateAuthCodes(newCodes);
            setNewAuthCode('');
            showNotification('Authorization code added.');
        } catch(e) {
            showNotification('Failed to add auth code.', 'error');
            console.error(e);
        }
    }
  };

  const handleRemoveCode = async (code: string) => {
    const newCodes = { ...authCodes };
    delete newCodes[code];
    try {
        await updateAuthCodes(newCodes);
        showNotification('Authorization code removed.');
    } catch(e) {
        showNotification('Failed to remove auth code.', 'error');
        console.error(e);
    }
  };

  const handleClearHistory = async (type: 'reports' | 'logins' | 'sessions') => {
    const confirmation = window.prompt(`This is a destructive action. To confirm, type "DELETE ${type.toUpperCase()}"`);
    if (confirmation === `DELETE ${type.toUpperCase()}`) {
      try {
        if (type === 'reports') await clearReportsHistory();
        if (type === 'logins') await clearLoginHistory();
        if (type === 'sessions') await clearNewSessionHistory();
        showNotification(`${type} history cleared successfully.`, 'success');
      } catch (error) {
        showNotification(`Failed to clear ${type} history.`, 'error');
      }
    } else {
        showNotification("Action cancelled.", 'error');
    }
  };

  const handleClearNews = () => {
    if (window.confirm("Are you sure you want to clear all announcements?")) {
        clearNewsFeed();
        showNotification("Announcements cleared.", "success");
    }
  };

  const handleUpdatePercentage = async () => {
    const percentage = Number(newScanPercentage);
    if (!isNaN(percentage) && percentage >= 0 && percentage <= 100) {
        try {
            await updateSecurityScanPercentage(percentage);
            showNotification('Security scan percentage updated.', 'success');
        } catch (e) {
            showNotification('Failed to update percentage.', 'error');
            console.error(e);
        }
    } else {
        showNotification('Please enter a valid percentage (0-100).', 'error');
    }
  };

  const handleSuperAdminPasswordChange = () => {
    if (currentUser && superAdminPassword.length >= 6) {
        updateUserPassword(currentUser.id, superAdminPassword);
        setSuperAdminPassword('');
        showNotification("Super admin password updated.", "success");
    } else {
        showNotification("Password must be at least 6 characters.", "error");
    }
  };

  const handleSaveVapidKey = async () => {
    if (vapidKeyInput.trim()) {
        await updateVapidPublicKey(vapidKeyInput.trim());
        showNotification('VAPID Public Key saved.', 'success');
    } else {
        showNotification('VAPID key cannot be empty.', 'error');
    }
  };

  const formatDateHeader = (dateStr: string): string => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const reportDate = new Date(dateStr);

    today.setHours(0, 0, 0, 0);
    yesterday.setHours(0, 0, 0, 0);
    reportDate.setHours(0, 0, 0, 0);

    let prefix = '';
    if (reportDate.getTime() === today.getTime()) {
        prefix = 'Today - ';
    } else if (reportDate.getTime() === yesterday.getTime()) {
        prefix = 'Yesterday - ';
    }
    
    return prefix + new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC'
    });
  };

  return (
    <div className="space-y-6">
      <UserManagementPanel users={users} currentUser={currentUser} isSuperAdmin={isSuperAdmin} onShowCreateUser={() => setShowCreateUser(true)} />

      {isSuperAdmin && (
        <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
            <h2 className="text-xl font-bold text-blue-400 mb-4 border-b border-slate-700 pb-2">App Branding &amp; Icons</h2>
            <div className="flex flex-wrap -m-3">
                <div className="w-full lg:w-1/3 p-3">
                    <h3 className="text-lg font-semibold text-white mb-2">App Icon &amp; PWA Manifest</h3>
                    <div className="bg-slate-700/50 p-4 rounded-lg flex flex-col items-center text-center h-full">
                        <p className="text-sm text-slate-300 flex-grow mb-4">Generate PWA-compliant icons for all platforms (iOS, Android, PC) from a single image. This will set the favicon and home screen icons.</p>
                        <button onClick={() => setShowIconWizard(true)} className="mt-auto bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-bold py-2 px-4 rounded-lg">
                            Open Icon Generator
                        </button>
                    </div>
                </div>
                <div className="w-full lg:w-2/3 p-3">
                    <h3 className="text-lg font-semibold text-white mb-2">In-App Assets</h3>
                    <div className="flex flex-wrap -m-2">
                        <div className="w-1/2 sm:w-1/3 xl:w-1/4 p-2 h-full"><AssetUploader assetKey="animatedLogo" assetUrl={appAssets.animatedLogo} updateAsset={updateAsset} assetType="image" label="Animated App Logo" showNotification={showNotification} keepTransparency={true} /></div>
                        <div className="w-1/2 sm:w-1/3 xl:w-1/4 p-2 h-full"><AssetUploader assetKey="logo1" assetUrl={appAssets.logo1} updateAsset={updateAsset} assetType="image" label="Primary Logo" showNotification={showNotification} keepTransparency={true} /></div>
                        <div className="w-1/2 sm:w-1/3 xl:w-1/4 p-2 h-full"><AssetUploader assetKey="logo2" assetUrl={appAssets.logo2} updateAsset={updateAsset} assetType="image" label="Secondary Logo" showNotification={showNotification} keepTransparency={true} /></div>
                        <div className="w-1/2 sm:w-1/3 xl:w-1/4 p-2 h-full"><AssetUploader assetKey="notificationSound" assetUrl={appAssets.notificationSound} updateAsset={updateAsset} assetType="audio" label="Notification Sound" showNotification={showNotification}/></div>
                    </div>
                     <p className="text-xs text-slate-500 mt-2 text-center">The Animated App Logo is used on the login screen. The Icon Generator output is used for favicons and the small header logo.</p>
                </div>
            </div>
            <p className="text-xs text-slate-500 mt-4 text-center">Changes are saved automatically and may require an app refresh to apply everywhere.</p>
        </div>
      )}

      {isAdmin && (
        <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
          <h2 className="text-xl font-bold mb-4 text-blue-400 border-b border-slate-700 pb-2">Flight Number Management</h2>
          <div>
              <h3 className="text-lg font-semibold text-white mb-2">Add New Flight Number</h3>
              <div className="flex flex-col sm:flex-row items-stretch gap-2 mb-4">
                  <input type="text" value={newFlightNumber} onChange={e => setNewFlightNumber(e.target.value.toUpperCase())} placeholder="e.g., JT-123" className="flex-grow py-2 px-3"/>
                  <button onClick={addFlightNumber} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg">Add</button>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Existing Flight Numbers</h3>
              <div className="space-y-2 max-h-32 overflow-y-auto pr-2">
                  {flightNumbers.map(flight => (
                      <div key={flight} className="flex justify-between items-center bg-slate-700 p-2 rounded">
                          <span className="font-mono text-white">{flight}</span>
                          <button onClick={() => removeFlightNumber(flight)} className="text-red-500 hover:text-red-400 text-xs">Remove</button>
                      </div>
                  ))}
              </div>
          </div>
        </div>
      )}
      
      {isAdmin && (
        <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
          <h2 className="text-xl font-bold mb-4 text-blue-400 border-b border-slate-700 pb-2">System Settings</h2>
          <div className="flex flex-wrap -mx-3 -my-4">
              <div className="w-full md:w-1/2 lg:w-1/3 px-3 py-4">
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">Authorization Codes</h3>
                    <div className="flex items-stretch gap-2">
                        <input type="text" value={newAuthCode} onChange={e => setNewAuthCode(e.target.value.toUpperCase())} placeholder="New Auth Code" className="flex-grow py-2 px-3"/>
                        <select value={selectedRoleForCode} onChange={(e) => setSelectedRoleForCode(e.target.value as UserRole)} className="py-2 px-3">
                            {Object.values(UserRole).filter(r => r !== UserRole.ADMIN).map(role => <option key={role} value={role}>{role}</option>)}
                        </select>
                        <button onClick={handleAddCode} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg">Add</button>
                    </div>
                    <div className="space-y-2 max-h-32 overflow-y-auto pr-2">
                        {Object.entries(authCodes).map(([code, role]) => (
                            <div key={code} className="flex justify-between items-center bg-slate-700 p-2 rounded">
                                <div>
                                    <span className="font-mono text-white">{code}</span>
                                    <span className={`ml-4 text-xs px-2 py-1 rounded-full ${role === 'SUPERVISOR' ? 'bg-amber-800 text-amber-200' : 'bg-blue-800 text-blue-200'}`}>{role}</span>
                                </div>
                                <button onClick={() => handleRemoveCode(code)} className="text-red-500 hover:text-red-400 text-xs">Remove</button>
                            </div>
                        ))}
                    </div>
                </div>
              </div>
              <div className="w-full md:w-1/2 lg:w-1/3 px-3 py-4">
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">Registration</h3>
                    <button onClick={toggleRegistration} className={`w-full px-4 py-2 rounded-lg text-sm font-semibold text-white ${isRegistrationOpen ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}>
                        {isRegistrationOpen ? 'Close Registration' : 'Open Registration'}
                    </button>
                </div>
              </div>
              <div className="w-full md:w-1/2 lg:w-1/3 px-3 py-4">
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">Multi-Flight Mode</h3>
                    <button onClick={toggleMultiFlight} className={`w-full px-4 py-2 rounded-lg text-sm font-semibold text-white ${isMultiFlightEnabled ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}>
                        {isMultiFlightEnabled ? 'Disable Multi-Flight' : 'Enable Multi-Flight'}
                    </button>
                    <p className="text-xs text-slate-400 mt-1">{isMultiFlightEnabled ? 'Users can manage up to 3 flights simultaneously.' : 'Users can only manage one flight at a time.'}</p>
                </div>
              </div>
              <div className="w-full md:w-1/2 lg:w-1/3 px-3 py-4">
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">Security Scan Percentage</h3>
                    <p className="text-sm text-slate-400">Set the percentage of total passengers to be scanned for the ETD sheet.</p>
                    <div className="flex items-center gap-2">
                        <input 
                            type="number" 
                            value={newScanPercentage} 
                            onChange={e => setNewScanPercentage(Number(e.target.value))} 
                            min="0"
                            max="100"
                            className="w-24 py-2 px-3"
                        />
                        <span className="text-white font-bold">%</span>
                        <button onClick={handleUpdatePercentage} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg">Save</button>
                    </div>
                </div>
              </div>
              {isSuperAdmin && (
                <div className="w-full lg:w-2/3 px-3 py-4">
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-white">Push Notification Settings</h3>
                        <p className="text-sm text-slate-400">
                            Enter the VAPID **public** key to enable web push notifications. The corresponding private key must be used in your backend service (e.g., Firebase Cloud Function) to send messages.
                        </p>
                        <div className="flex items-stretch gap-2">
                            <input 
                                type="text" 
                                value={vapidKeyInput} 
                                onChange={e => setVapidKeyInput(e.target.value)} 
                                placeholder="Enter VAPID Public Key"
                                className="w-full"
                            />
                            <button onClick={handleSaveVapidKey} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg">Save Key</button>
                        </div>
                        <p className="text-xs text-slate-500 mt-1"><strong>Important:</strong> After saving the key, users must individually enable push notifications via the bell icon in the top bar.</p>
                    </div>
                </div>
              )}
          </div>
        </div>
      )}
      
       <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <h2 className="text-xl font-bold text-blue-400">Announcements</h2>
            <button
                onClick={handleClearNews}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-3 rounded-lg flex items-center text-sm"
            >
                <TrashIcon className="w-4 h-4 mr-2" />
                Clear All
            </button>
        </div>
        <p className="text-sm text-slate-400 mb-4">
          Send a message to the news bar visible to all active users. This will also send a push notification.
        </p>
        <NewsInputField />
      </div>

      <div className="flex flex-wrap -m-3">
        <div className="w-full lg:w-1/2 p-3">
            <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4 flex flex-col h-full">
            <h2 className="text-xl font-bold mb-4 text-blue-400">Login History</h2>
            <div className="flex-grow overflow-y-auto">
                <div className="overflow-x-auto">
                <table className="min-w-full">
                    <thead className="bg-slate-700/50 sticky top-0 z-20">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">User</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Action</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Location / IP</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Timestamp</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                        {sortedHistoryDates.length > 0 ? (
                        sortedHistoryDates.map(date => (
                            <React.Fragment key={date}>
                                <tr>
                                    <td colSpan={4} className="bg-slate-800/90 backdrop-blur-sm font-semibold text-amber-300 py-1 px-4 sticky top-0 z-10">
                                        {formatDateHeader(date)}
                                    </td>
                                </tr>
                                {groupedLoginHistory[date].map(log => (
                                    <tr key={log.key} className="hover:bg-slate-800/50">
                                        <td className="px-6 py-3 whitespace-nowrap text-sm text-white">{log.username}</td>
                                        <td className="px-6 py-3 whitespace-nowrap text-sm">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${log.action === 'login' ? 'bg-green-900 text-green-200' : 'bg-red-900 text-red-200'}`}>
                                                {log.action}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3 whitespace-nowrap text-xs text-slate-400">
                                            {log.action === 'login' ? (
                                                log.ip ? (
                                                    <>
                                                        <div>{log.ip}</div>
                                                        <div className="max-w-[150px] truncate" title={[log.city, log.region, log.countryName].filter(Boolean).join(', ')}>
                                                            {[log.city, log.region, log.countryName].filter(Boolean).join(', ')}
                                                        </div>
                                                    </>
                                                ) : (
                                                    'N/A'
                                                )
                                            ) : null}
                                        </td>
                                        <td className="px-6 py-3 whitespace-nowrap text-sm text-slate-400">{new Date(log.timestamp).toLocaleString('en-GB')}</td>
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={4} className="text-center py-10 text-slate-400">
                                No login history found.
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
                </div>
            </div>
            </div>
        </div>

        <div className="w-full lg:w-1/2 p-3">
            <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4 flex flex-col h-full">
            <h2 className="text-xl font-bold mb-4 text-blue-400">Session Start History</h2>
            <div className="flex-grow overflow-y-auto">
                <div className="overflow-x-auto">
                    <table className="min-w-full">
                        <thead className="bg-slate-700/50 sticky top-0">
                            <tr>
                                <th className="px-6 py-2 text-left text-xs font-medium text-slate-300 uppercase">User</th>
                                <th className="px-6 py-2 text-left text-xs font-medium text-slate-300 uppercase">Timestamp</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700">
                            {sessionHistory.map(log => (
                                <tr key={log.key} className="hover:bg-slate-800/50">
                                    <td className="px-6 py-3 whitespace-nowrap text-sm text-white">{log.username}</td>
                                    <td className="px-6 py-3 whitespace-nowrap text-sm text-slate-400">{new Date(log.timestamp).toLocaleString('en-GB')}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            </div>
        </div>
      </div>


       {isSuperAdmin && (
          <div className="bg-slate-900 border-2 border-red-500/50 p-4 rounded-lg shadow-md">
            <h2 className="text-xl font-bold mb-4 text-red-400">Super Admin Danger Zone</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                     <h3 className="text-lg font-semibold text-white">Change Your Password</h3>
                      <div className="flex items-stretch gap-2">
                        <input type="password" value={superAdminPassword} onChange={(e) => setSuperAdminPassword(e.target.value)} className="w-full" placeholder="New Password"/>
                        <button onClick={handleSuperAdminPasswordChange} className="bg-amber-600 hover:bg-amber-700 text-white font-bold py-2 px-4 rounded-lg">Save</button>
                     </div>
                </div>
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">Clear History</h3>
                     <div className="flex flex-col sm:flex-row gap-4">
                        <button onClick={() => handleClearHistory('reports')} className="bg-red-700 hover:bg-red-800 text-white font-bold py-2 px-4 rounded-lg w-full">Delete All Reports</button>
                        <button onClick={() => handleClearHistory('logins')} className="bg-red-700 hover:bg-red-800 text-white font-bold py-2 px-4 rounded-lg w-full">Delete Login History</button>
                        <button onClick={() => handleClearHistory('sessions')} className="bg-red-700 hover:bg-red-800 text-white font-bold py-2 px-4 rounded-lg w-full">Delete Session History</button>
                     </div>
                </div>
            </div>
          </div>
       )}

      {/* Modals */}
      {showCreateUser && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-slide-up-fade-in">
          <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-lg border border-slate-700">
            <h3 className="text-xl font-semibold text-white mb-4">Create New User</h3>
            <div className="space-y-4">
              <input type="text" placeholder="Username*" value={newUser.username} onChange={(e) => setNewUser({...newUser, username: e.target.value})} className="w-full" />
              <input type="password" placeholder="Password*" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} className="w-full" />
              <input type="email" placeholder="Email (Optional)" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} className="w-full" />
              <select value={newUser.role} onChange={(e) => setNewUser({...newUser, role: e.target.value as UserRole})} className="w-full">
                  {Object.values(UserRole)
                    .filter(role => isSuperAdmin || role !== UserRole.ADMIN)
                    .map(role => <option key={role} value={role}>{role}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-4 mt-6 pt-4 border-t border-slate-700">
              <button onClick={() => setShowCreateUser(false)} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-4 rounded-lg transition-colors">Cancel</button>
              <button onClick={handleCreateUser} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg transition-colors">Save User</button>
            </div>
          </div>
        </div>
      )}
      {showIconWizard && <IconWizard onClose={() => setShowIconWizard(false)} />}
    </div>
  );
};

export default AdminScreen;